import { Form, Input } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useEditUserMutation, useGetUserQuery } from '../../api/user-api';
import { currentUserAlertId, currentUserFormModalId } from '../../constants/constants';
import { User } from '../../models/user';
import { selectActiveCurrentUserId } from '../../store/current-user-slice';

const CurrentUserEditModal = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['current-user', 'common']);
  const dispatch = useDispatch();
  const activeCurrentUserId = useAppSelector(selectActiveCurrentUserId);

  const [form] = Form.useForm<Pick<User, 'name'>>();

  const [editCurrentUser, { isLoading: isSubmitting }] = useEditUserMutation();

  const { data: currentUserData } = useGetUserQuery();

  const closeUserEditModal = () => {
    dispatch(closeModal(currentUserFormModalId));
  };

  const submitForm = (formData: Pick<User, 'name'>) => {
    if (!activeCurrentUserId) {
      return;
    }

    editCurrentUser({
      ...formData,
      id: activeCurrentUserId,
    })
      .unwrap()
      .then(() => {
        closeUserEditModal();
        dispatch(
          setAlert({
            id: currentUserAlertId,
            type: 'success',
            title: t('editUserInfo.message.editSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors || { email: [t('errors.unexpected', { ns: 'common' })] });
      });
  };

  const initialValues: Pick<User, 'name'> = useMemo(() => {
    return {
      name: currentUserData?.name || '',
    };
  }, [currentUserData]);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Modal
      title={t('editUserInfo.label.editUsersFullName')}
      onCancel={() => !isSubmitting && closeUserEditModal()}
      isLoading={isSubmitting}
      onOk={() => form.submit()}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Form form={form} layout="vertical" initialValues={initialValues} onFinish={submitForm}>
        <Form.Item
          name="name"
          label={t('label.name', { ns: 'common' })}
          rules={[{ required: true, message: t('errors.nameRequired', { ns: 'common' }) }]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CurrentUserFormModal = (): JSX.Element => {
  return <LazyModal modalId={currentUserFormModalId} Component={CurrentUserEditModal} />;
};

export default CurrentUserFormModal;
